import React from "react";
import { Icon } from "./Icon";
import { Link } from "react-router-dom";
import { MarketingHeader } from "./MarketingHeader";
import { PageMeta } from "./PageMeta";

const handleLogin = () => {
  window.location.href = "/api/auth/twitch";
};

const PURPLE = "#9146FF";
const CYAN = "#00D4FF";
const BG = "#0a0a0a";
const TEXT = "#e2e8f0";
const MUTED = "#a0aec0";

interface Pick {
  name: string;
  bestFor: string;
  blurb: string;
  highlight?: boolean;
}

// Honest roundup: HAL is the AI-native pick; the incumbents are (correctly)
// credited for what they're actually best at. Credibility > puffery.
const PICKS: Pick[] = [
  {
    name: "HAL 8580",
    bestFor: "Best AI personality & memory",
    highlight: true,
    blurb:
      "The only pick here that's genuinely an AI co-host, not a command runner. Choose a personality (dry classic, sarcastic HAL DARK, or premium personas), and HAL remembers your regulars, reads the stream, and reacts to raids, subs and follows in its own voice. Free tier; Premium is $4.99/mo for extra personalities and analytics.",
  },
  {
    name: "ai-licia",
    bestFor: "Best-known AI co-host",
    blurb:
      "One of the most established AI co-hosts on Twitch — a talkative on-stream companion with voice and a personality, freemium with paid tiers. Strong if you want a single polished AI persona. HAL's angle is switchable personalities (including the dry, sarcastic HAL DARK), memory of your regulars, and event reactions on a free tier.",
  },
  {
    name: "StreamChat AI",
    bestFor: "Best simple AI replies",
    blurb:
      "An AI chat bot for Twitch and Kick focused on generated replies, with a free trial. Clean and easy to start. HAL differs by giving you a choice of personalities, viewer memory, and in-character reactions to raids, subs and follows rather than replies alone.",
  },
  {
    name: "Nightbot",
    bestFor: "Best for simple moderation & timers",
    blurb:
      "The default first bot for a reason: rock-solid, free, set-and-forget. Commands, timers, spam filters, song requests. No AI or personality — it prints what you pre-write. Pairs well with HAL (Nightbot for housekeeping, HAL for character).",
  },
  {
    name: "StreamElements",
    bestFor: "Best all-in-one production suite",
    blurb:
      "Overlays, alerts, loyalty points, tipping and a chatbot in one free platform. Enormous feature set for on-screen production — but the chat side is command-based, not conversational. Run it for overlays and add HAL for the AI chat personality.",
  },
  {
    name: "Fossabot",
    bestFor: "Best modern moderation",
    blurb:
      "A newer, fast, reliable moderation and commands bot that many mid-size streamers have moved to. Clean interface, strong spam tooling. Still a command bot at heart — no AI personality or viewer memory.",
  },
  {
    name: "DIY ChatGPT bots",
    bestFor: "Best if you want to build it yourself",
    blurb:
      "You can wire an LLM into Twitch chat yourself. Maximum control, but you own the hosting, prompts, moderation, memory and uptime. HAL is essentially this, productised — the personality, memory and event reactions already built and hosted.",
  },
];

const faqs = [
  {
    q: "What is the best free Twitch AI chatbot in 2026?",
    a: "For an AI chatbot with an actual personality and memory of your viewers, HAL 8580 is the strongest free pick — it's built AI-first rather than being a command bot with AI bolted on. For pure moderation and timers, Nightbot and Fossabot remain excellent and free.",
  },
  {
    q: "What's the difference between an AI chatbot and a normal Twitch bot?",
    a: "A normal Twitch bot (Nightbot, StreamElements, Fossabot) runs commands and timers — it prints text you pre-wrote. An AI chatbot like HAL 8580 understands context, holds a personality, remembers regulars, and generates replies and reactions on its own.",
  },
  {
    q: "Can I run an AI chatbot alongside my current bot?",
    a: "Yes. HAL 8580 runs alongside Nightbot, StreamElements or Fossabot without conflict — keep your existing bot for moderation and use HAL as the AI co-host in chat.",
  },
  {
    q: "Is HAL 8580 really free?",
    a: "Yes, there's a free tier. Premium ($4.99/mo) unlocks extra personalities and 7- and 30-day chat analytics. You can add it to your channel in about two minutes.",
  },
];

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const BestTwitchAIChatbot: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 860);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div
      style={{
        backgroundColor: BG,
        color: TEXT,
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <PageMeta
        title="Best Free Twitch AI Chatbot (2026) — Ranked & Compared"
        description="The best AI chatbots for Twitch in 2026, honestly ranked. AI personality and viewer memory vs classic command bots like Nightbot, StreamElements and Fossabot."
        path="/best-twitch-ai-chatbot"
        breadcrumbs={[{ name: "Best Twitch AI Chatbot" }]}
        jsonLd={faqLd}
      />

      <MarketingHeader onLogin={handleLogin} />

      <section
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: isMobile ? "32px 20px 64px" : "48px 60px 96px",
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "36px" : "52px",
            fontWeight: 700,
            margin: "0 0 20px 0",
            letterSpacing: "-1.5px",
            lineHeight: 1.1,
            background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Best free Twitch AI chatbot (2026)
        </h1>
        <p
          style={{
            color: MUTED,
            fontSize: "18px",
            margin: "0 0 40px 0",
            fontWeight: 300,
            lineHeight: 1.6,
            maxWidth: "740px",
          }}
        >
          Most &quot;Twitch bots&quot; are command runners — brilliant at
          moderation and timers, but they print text you pre-wrote. The AI
          chatbot category is different: a bot with a personality that remembers
          your viewers and reacts on its own. Here&apos;s the honest ranking,
          including where the classic bots still win.
        </p>

        <button onClick={handleLogin} style={ctaButton}>
          <Icon name="gamepad-2" size={18} color="white" />
          Add HAL free — 2 minutes
        </button>

        <div style={{ display: "grid", gap: "16px", marginTop: "48px" }}>
          {PICKS.map((p, i) => (
            <div
              key={p.name}
              style={{
                padding: isMobile ? "20px" : "24px 28px",
                borderRadius: "14px",
                background: p.highlight
                  ? "rgba(145, 70, 255, 0.08)"
                  : "rgba(255, 255, 255, 0.02)",
                border: p.highlight
                  ? "1px solid rgba(145, 70, 255, 0.4)"
                  : "1px solid rgba(145, 70, 255, 0.12)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{ color: MUTED, fontSize: "15px", fontWeight: 700 }}
                >
                  {i + 1}.
                </span>
                <h2
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    margin: 0,
                    color: p.highlight ? PURPLE : TEXT,
                  }}
                >
                  {p.name}
                </h2>
                <span
                  style={{
                    color: CYAN,
                    fontSize: "13px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {p.bestFor}
                </span>
              </div>
              <p
                style={{
                  color: "#cbd5e0",
                  fontSize: "15px",
                  lineHeight: 1.7,
                  fontWeight: 300,
                  margin: 0,
                }}
              >
                {p.blurb}
              </p>
              {p.highlight && (
                <div style={{ marginTop: "16px" }}>
                  <button onClick={handleLogin} style={ctaButtonSmall}>
                    <Icon name="gamepad-2" size={16} color="white" />
                    Try HAL free
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: "64px" }}>
          <h2 style={h2Style}>Common questions</h2>
          <div style={{ display: "grid", gap: "12px", maxWidth: "760px" }}>
            {faqs.map((f) => (
              <details
                key={f.q}
                style={{
                  padding: "18px 22px",
                  borderRadius: "12px",
                  background: "rgba(145, 70, 255, 0.04)",
                  border: "1px solid rgba(145, 70, 255, 0.12)",
                }}
              >
                <summary
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: TEXT,
                    cursor: "pointer",
                    listStyle: "none",
                    outline: "none",
                  }}
                >
                  {f.q}
                </summary>
                <p
                  style={{
                    color: "#cbd5e0",
                    marginTop: "14px",
                    marginBottom: 0,
                    fontSize: "15px",
                    lineHeight: 1.7,
                    fontWeight: 300,
                  }}
                >
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "56px" }}>
          <p
            style={{
              color: MUTED,
              fontSize: "14px",
              fontWeight: 300,
            }}
          >
            Head-to-head:{" "}
            <Link style={linkStyle} to="/vs/nightbot">
              HAL vs Nightbot
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/vs/streamelements">
              HAL vs StreamElements
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/vs/ai-licia">
              HAL vs ai-licia
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/faq">
              FAQ
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

const ctaButton: React.CSSProperties = {
  background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
  color: "white",
  border: "none",
  padding: "14px 28px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "12px",
  cursor: "pointer",
  boxShadow: "0 8px 25px rgba(145, 70, 255, 0.4)",
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
};

const ctaButtonSmall: React.CSSProperties = {
  ...ctaButton,
  padding: "10px 18px",
  fontSize: "14px",
  boxShadow: "none",
};

const h2Style: React.CSSProperties = {
  fontSize: "26px",
  fontWeight: 700,
  color: TEXT,
  margin: "0 0 16px 0",
  letterSpacing: "-0.5px",
};

const linkStyle: React.CSSProperties = {
  color: CYAN,
  textDecoration: "none",
};
